import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Plus, 
  Trash2, 
  Edit3, 
  X, 
  CheckCircle, 
  AlertCircle,
  Upload,
  Download,
  FileText,
  Camera,
  CreditCard,
  Hash,
  Shield,
  Fingerprint,
  File,
  Image as ImageIcon,
  Loader,
  Building,
  Users,
  Database,
  Info
} from 'lucide-react';
import DocumentFieldsService from './DocumentFieldsService';
import { useAuth } from '../contexts/AuthContext'; // Correct import path

const ImageFieldsSettings = () => {
  const { currentUser, currentCompany, getCompanyContext } = useAuth();
  const [documentFields, setDocumentFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [tenantInfo, setTenantInfo] = useState(null);

  // Icon options
  const iconOptions = [
    { name: 'File', icon: File, value: 'File' },
    { name: 'FileText', icon: FileText, value: 'FileText' },
    { name: 'Camera', icon: Camera, value: 'Camera' },
    { name: 'CreditCard', icon: CreditCard, value: 'CreditCard' },
    { name: 'Hash', icon: Hash, value: 'Hash' },
    { name: 'Shield', icon: Shield, value: 'Shield' },
    { name: 'Fingerprint', icon: Fingerprint, value: 'Fingerprint' },
    { name: 'Upload', icon: Upload, value: 'Upload' },
    { name: 'Image', icon: ImageIcon, value: 'Image' }
  ];

  // Color options
  const colorOptions = [
    { name: 'Blue', value: 'blue-500' },
    { name: 'Green', value: 'green-500' },
    { name: 'Orange', value: 'orange-500' },
    { name: 'Purple', value: 'purple-500' },
    { name: 'Red', value: 'red-500' },
    { name: 'Indigo', value: 'indigo-500' },
    { name: 'Pink', value: 'pink-500' },
    { name: 'Teal', value: 'teal-500' }
  ];

  // Add custom scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #1F2937;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #4B5563;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #6B7280;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Initialize multi-tenant service
  useEffect(() => {
    const initializeTenantService = async () => {
      if (!currentCompany || !currentUser) {
        console.log('⏳ Waiting for auth context...');
        return;
      }

      try {
        console.log('🏢 Initializing DocumentFieldsService for company:', currentCompany.name, '(ID:', currentCompany.id, ')');
        
        // Set tenant context in the service
        DocumentFieldsService.setTenant(currentCompany.id, currentCompany.name);
        
        // Get tenant cache info for debugging
        const cacheInfo = DocumentFieldsService.getTenantCacheInfo();
        setTenantInfo({
          companyId: currentCompany.id,
          companyName: currentCompany.name,
          cacheInfo
        });
        
        console.log('📊 Tenant cache info:', cacheInfo);
        
        setIsLoading(false);
      } catch (error) {
        console.error('❌ Error initializing tenant service:', error);
        setError(`Failed to initialize for company ${currentCompany.name}: ${error.message}`);
        setIsLoading(false);
      }
    };

    initializeTenantService();
  }, [currentCompany, currentUser]);

  // Initialize subscription after tenant is set
  useEffect(() => {
    if (!tenantInfo || isLoading) {
      return;
    }

    let unsubscribe;

    const setupSubscription = () => {
      try {
        console.log('🔄 Setting up subscription for tenant:', tenantInfo.companyId);
        
        unsubscribe = DocumentFieldsService.subscribe((fields) => {
          console.log('📥 Received fields update for tenant:', tenantInfo.companyId, fields);
          setDocumentFields(fields);
          setStatistics(DocumentFieldsService.getFieldStatistics());
        });

        console.log('✅ Subscription established for tenant:', tenantInfo.companyId);
      } catch (error) {
        console.error('❌ Error setting up subscription:', error);
        setError(`Failed to subscribe to updates: ${error.message}`);
      }
    };

    // Small delay to ensure service is ready
    const timeoutId = setTimeout(setupSubscription, 100);

    return () => {
      clearTimeout(timeoutId);
      if (unsubscribe) {
        console.log('🔄 Cleaning up subscription for tenant:', tenantInfo.companyId);
        unsubscribe();
      }
    };
  }, [tenantInfo, isLoading]);

  // Show messages
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(''), 5000);
  };

  // Handle operations with loading state
  const handleOperation = async (operation, successMsg) => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      await operation();
      if (successMsg) showSuccess(successMsg);
    } catch (error) {
      console.error('Operation failed:', error);
      showError(error.message || 'Operation failed');
    } finally {
      setIsSaving(false);
    }
  };

  // Field operations
  const toggleField = (fieldId) => {
    handleOperation(
      () => DocumentFieldsService.toggleField(fieldId),
      'Field updated'
    );
  };

  const updateField = (fieldId, updates) => {
    handleOperation(
      () => DocumentFieldsService.updateField(fieldId, updates),
      'Field updated'
    );
    setEditingField(null);
  };

  const addField = (fieldData) => {
    if (!fieldData.label?.trim()) {
      showError('Field label is required');
      return;
    }
    
    handleOperation(
      () => DocumentFieldsService.addCustomField(fieldData),
      'Field added'
    );
    setShowAddModal(false);
  };

  const removeField = (fieldId) => {
    const defaultFieldIds = ['license', 'selfie', 'nic', 'passport'];
    if (defaultFieldIds.includes(fieldId)) {
      showError('Cannot delete default fields. You can disable them instead.');
      return;
    }

    setConfirmDialog({
      show: true,
      title: 'Remove Field',
      message: 'Are you sure you want to remove this custom field?',
      onConfirm: () => {
        handleOperation(
          () => DocumentFieldsService.removeField(fieldId),
          'Field removed'
        );
        setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const enableAllFields = () => {
    handleOperation(
      () => DocumentFieldsService.enableAllFields(),
      'All fields enabled'
    );
  };

  const disableAllFields = () => {
    setConfirmDialog({
      show: true,
      title: 'Disable All Fields',
      message: 'Disable all document fields?',
      onConfirm: () => {
        handleOperation(
          () => DocumentFieldsService.disableAllFields(),
          'All fields disabled'
        );
        setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const exportConfiguration = () => {
    try {
      const config = DocumentFieldsService.exportConfiguration();
      const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document-fields-${currentCompany.name.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccess('Configuration exported');
    } catch (error) {
      console.error('Export failed:', error);
      showError('Export failed');
    }
  };

  const importConfiguration = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const config = JSON.parse(text);
      
      // Validate that this is a valid config
      if (!config.fields || !Array.isArray(config.fields)) {
        throw new Error('Invalid configuration file format');
      }
      
      // Show warning if importing from different tenant
      if (config.tenantId && config.tenantId !== currentCompany.id) {
        const confirmImport = window.confirm(
          `⚠️ This configuration is from a different company.\n\n` +
          `Current: ${currentCompany.name}\n` +
          `Import from: ${config.tenantId}\n\n` +
          `Continue with import?`
        );
        
        if (!confirmImport) {
          event.target.value = '';
          return;
        }
      }
      
      await DocumentFieldsService.importConfiguration(config);
      showSuccess('Configuration imported successfully');
    } catch (error) {
      console.error('Import failed:', error);
      showError(`Import failed: ${error.message}`);
    }
    event.target.value = '';
  };

  // Edit Modal
  const EditModal = ({ field, onSave, onClose }) => {
    const [editData, setEditData] = useState({ ...field });

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 my-auto">
          <h3 className="text-lg font-semibold text-white mb-4">Edit Field</h3>
          
          <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Label</label>
              <input
                type="text"
                value={editData.label}
                onChange={(e) => setEditData(prev => ({ ...prev, label: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Icon</label>
              <select
                value={editData.icon}
                onChange={(e) => setEditData(prev => ({ ...prev, icon: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              >
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Color</label>
              <select
                value={editData.color}
                onChange={(e) => setEditData(prev => ({ ...prev, color: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              >
                {colorOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.enabled}
                  onChange={(e) => setEditData(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-300">Enabled</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editData.required}
                  onChange={(e) => setEditData(prev => ({ ...prev, required: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-300">Required</span>
              </label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(editData)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Modal
  const AddModal = ({ onAdd, onClose }) => {
    const [newField, setNewField] = useState({
      label: '',
      icon: 'File',
      color: 'blue-500',
      enabled: false,
      required: false
    });

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 my-auto">
          <h3 className="text-lg font-semibold text-white mb-4">Add New Field</h3>
          
          <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
            <div>
              <label className="block text-sm text-gray-300 mb-1">Label</label>
              <input
                type="text"
                value={newField.label}
                onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., Passport Copy"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Icon</label>
              <select
                value={newField.icon}
                onChange={(e) => setNewField(prev => ({ ...prev, icon: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              >
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Color</label>
              <select
                value={newField.color}
                onChange={(e) => setNewField(prev => ({ ...prev, color: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              >
                {colorOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newField.enabled}
                  onChange={(e) => setNewField(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-300">Enabled</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newField.required}
                  onChange={(e) => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-300">Required</span>
              </label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => onAdd(newField)}
              disabled={!newField.label.trim()}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
            >
              Add Field
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Confirm Dialog
  const ConfirmDialog = ({ show, title, message, onConfirm, onCancel }) => {
    if (!show) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 my-auto">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="text-yellow-400 flex-shrink-0" size={24} />
            <h3 className="text-lg font-semibold text-white">{title}</h3>
          </div>
          <p className="text-gray-300 mb-6 max-h-32 overflow-y-auto custom-scrollbar">{message}</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={onCancel || (() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null }))}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-8">
            <div className="flex items-center justify-center">
              <Loader className="animate-spin text-orange-500 mr-3" size={32} />
              <div>
                <h2 className="text-xl font-bold">Loading Multi-Tenant Service...</h2>
                <p className="text-gray-400">Initializing for {currentCompany?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-blue-900 text-white p-6 overflow-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Settings className="text-orange-500" />
            Document Field Settings
            <div className="ml-auto flex items-center text-sm text-blue-400">
              <Database size={16} className="mr-1" />
              Multi-Tenant
            </div>
          </h1>
          <p className="text-gray-300 mt-2">
            Configure document fields for <strong>{currentCompany?.name}</strong>. 
            All changes are isolated to your company's data.
          </p>
        </div>

        {/* Multi-Tenant Info Panel */}
        <div className="mb-6 bg-blue-900/30 border border-blue-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="text-blue-300 font-medium mb-2 flex items-center gap-2">
                <Building size={16} />
                Company Context: {currentCompany?.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-blue-200">Company ID:</span>
                  <div className="text-gray-300 font-mono text-xs">{currentCompany?.id}</div>
                </div>
                <div>
                  <span className="text-blue-200">Current User:</span>
                  <div className="text-gray-300">{currentUser?.username} ({currentUser?.role})</div>
                </div>
                <div>
                  <span className="text-blue-200">Data Isolation:</span>
                  <div className="text-green-300">✅ Active</div>
                </div>
              </div>
              {tenantInfo?.cacheInfo && (
                <div className="mt-2 text-xs text-blue-200">
                  Cache: {tenantInfo.cacheInfo.cacheSize} tenants loaded, 
                  Current: {tenantInfo.cacheInfo.currentTenant}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-white">{statistics.total || 0}</div>
            <div className="text-gray-400 text-sm">Total Fields</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">{statistics.enabled || 0}</div>
            <div className="text-gray-400 text-sm">Enabled</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-400">{statistics.required || 0}</div>
            <div className="text-gray-400 text-sm">Required</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-400">{statistics.disabled || 0}</div>
            <div className="text-gray-400 text-sm">Disabled</div>
          </div>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-900/50 border border-green-700 rounded-lg flex items-center">
            <CheckCircle className="text-green-400 mr-3" size={20} />
            <span className="text-green-300">{successMessage}</span>
            <button onClick={() => setSuccessMessage('')} className="ml-auto text-green-400">
              <X size={16} />
            </button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-lg flex items-center">
            <AlertCircle className="text-red-400 mr-3" size={20} />
            <span className="text-red-300">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex flex-wrap gap-3 overflow-x-auto pb-2 custom-scrollbar">
          <button
            onClick={() => setShowAddModal(true)}
            disabled={isSaving}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex-shrink-0"
          >
            <Plus size={16} />
            Add Field
          </button>
          
          <button
            onClick={enableAllFields}
            disabled={isSaving}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex-shrink-0"
          >
            <CheckCircle size={16} />
            Enable All
          </button>
          
          <button
            onClick={disableAllFields}
            disabled={isSaving}
            className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex-shrink-0"
          >
            <X size={16} />
            Disable All
          </button>
          
          <button
            onClick={exportConfiguration}
            disabled={isSaving}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex-shrink-0"
          >
            <Download size={16} />
            Export
          </button>
          
          <label className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg cursor-pointer flex-shrink-0">
            <Upload size={16} />
            Import
            <input
              type="file"
              accept=".json"
              onChange={importConfiguration}
              className="hidden"
            />
          </label>
        </div>

        {/* Fields List */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700">
          <div className="p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              Document Fields
              <span className="text-sm text-blue-400 font-normal">for {currentCompany?.name}</span>
            </h2>
            
            {documentFields.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-gray-300 font-medium mb-2">No Document Fields</h3>
                <p className="text-gray-500 text-sm">Default fields will appear here automatically.</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar">
                <div className="space-y-3">
                  {documentFields.map((field) => {
                    const IconComponent = iconOptions.find(opt => opt.value === field.icon)?.icon || File;
                    const isDefaultField = ['license', 'selfie', 'nic', 'passport'].includes(field.id);
                    
                    return (
                      <div
                        key={field.id}
                        className="flex items-center justify-between p-4 bg-gray-700 border border-gray-600 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <IconComponent 
                            size={20} 
                            className={field.enabled ? `text-${field.color}` : 'text-gray-500'}
                          />
                          <div>
                            <h3 className={`font-medium ${field.enabled ? 'text-white' : 'text-gray-400'}`}>
                              {field.label}
                              {field.required && <span className="text-red-400 ml-1">*</span>}
                              {isDefaultField && <span className="text-blue-400 ml-2 text-xs">(Default)</span>}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {field.enabled ? 'Enabled' : 'Disabled'} • 
                              {field.required ? ' Required' : ' Optional'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={field.enabled}
                              onChange={() => toggleField(field.id)}
                              disabled={isSaving}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          </label>
                          
                          <button
                            onClick={() => setEditingField(field)}
                            disabled={isSaving}
                            className="p-2 text-gray-400 hover:text-white transition-colors"
                          >
                            <Edit3 size={16} />
                          </button>
                          
                          <button
                            onClick={() => removeField(field.id)}
                            disabled={isSaving || isDefaultField}
                            className={`p-2 transition-colors ${
                              isDefaultField 
                                ? 'text-gray-600 cursor-not-allowed' 
                                : 'text-gray-400 hover:text-red-400'
                            }`}
                            title={isDefaultField ? 'Cannot delete default fields' : 'Delete field'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Loading Overlay */}
        {isSaving && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40">
            <div className="bg-gray-800 rounded-lg p-6 flex items-center border border-gray-700">
              <Loader className="animate-spin text-orange-500 mr-3" size={24} />
              <span className="text-white">Saving to {currentCompany?.name}...</span>
            </div>
          </div>
        )}

        {/* Modals */}
        {editingField && (
          <EditModal
            field={editingField}
            onSave={(data) => updateField(editingField.id, data)}
            onClose={() => setEditingField(null)}
          />
        )}

        {showAddModal && (
          <AddModal
            onAdd={addField}
            onClose={() => setShowAddModal(false)}
          />
        )}

        <ConfirmDialog
          show={confirmDialog.show}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
        />
      </div>
    </div>
  );
};

export default ImageFieldsSettings;